<template>
  <div class="assessment-navigator">
    <!-- {{ selectingId }} -->
    <!-- {{ selectedId }} -->
    <!-- {{ [...entryList].map((entry) => entry.target.id) }} -->
    <ul>
      <li v-for="(partMeta, partIndex) in partMetaData" :key="partIndex">
        <div
          :id="`part-${partTitleIndexMap[partMeta]}`"
          class="selectable-item"
          :class="{ selected: isPartSelected(partMeta) }"
          @click="navigateToPost('title', `part-${partTitleIndexMap[partMeta]}`)"
        >
          {{ partTitle(partMeta) }}
        </div>
        <ul v-if="partMeta === 'WORK_EXAMPLE'" class="part__work-example">
          <li
            v-for="(post, index) in getPostsByPart(partMeta)"
            :id="`part-${partTitleIndexMap[partMeta]}-task-${index + 1}`"
            :key="index"
            class="task-list"
            @click="navigateToPost('task', `part-${partTitleIndexMap[partMeta]}-task-${index + 1}`)"
          >
            <div
              class="selectable-item item"
              :class="{ selected: isSelected(`part-${partTitleIndexMap[partMeta]}-task-${index + 1}`) }"
            >
              <li class="task-list-item">
                <div class="text-truncate">{{ post.title }}</div>
              </li>
            </div>
          </li>
        </ul>
      </li>
      <li>
        <div
          class="selectable-item item"
          :class="{ selected: isSelected(`post-evaluation-assessment-submit`) }"
          @click="navigateToPost('addition', `post-evaluation-assessment-submit`)"
        >
          Feedback for Earner & Issuer
        </div>
      </li>
      <li>
        <div
          class="selectable-item item"
          :class="{ selected: isSelected(`confirmation-evaluation-assessment`) }"
          @click="navigateToPost('addition', `confirmation-evaluation-assessment`)"
        >
          Assessor's Confirmation
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import Post from '@/models/Post';

// Constants
const PARTS = {
  OVERVIEW: 'Overview',
  WORK_EXAMPLE: 'Work Examples',
  REFLECTION: 'Reflection'
};

const SCROLL_TIMEOUT = 600;

export default {
  name: 'AssessmentNavigator',
  props: {
    parts: {
      type: Object,
      required: true
    },
    partMetaData: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      selectingOverride: null,
      isScrolling: false,
      entryList: [],
      observers: [],
      partTitleIndexMap: {
        OVERVIEW: 1,
        WORK_EXAMPLE: 2,
        REFLECTION: 3
      },
    };
  },
  computed: {
    posts() {
      const { parts = {} } = this;
      const postIds = Object.values(parts).flat();
      return Post.query().whereIdIn(postIds).get();
    },
    selectedId() {
      if (!this.entryList.length) return null;

      const sortedEntries = this.getSortedEntries();
      const entryId = sortedEntries[0]?.target.id;

      return this.parseEntryIdToNavId(entryId);
    }
  },
  watch: {
    selectedId: {
      immediate: true,
      handler() {
        if (!this.isScrolling) {
          this.selectingOverride = null;
        }
      }
    }
  },
  mounted() {
    this.registerIntersectionObserver();
    this.observePostInView();
  },
  beforeDestroy() {
    this.disconnectObservers();
  },
  methods: {
    // Navigation methods
    navigateToPost(type, id) {
      console.log(`Navigation requested: type=${type}, id=${id}`);

      // Use a short delay to ensure DOM is ready
      setTimeout(() => {
        this.isScrolling = true;
        const elemId = this.getElementId(type, id);
        console.log(`Target element ID: ${elemId}`);

        this.scrollToElement(elemId, id);

        // Emit event to parent instead of directly manipulating DOM
        this.$emit('navigation-clicked', { type, id, targetElementId: elemId });
      }, 100);
    },

    getElementId(type, id) {
      if (type === 'addition') {
        return id;
      }

      const idParts = this.parsePostId(type, id);
      if (!idParts) {
        console.warn(`Failed to parse ID: ${id} with type ${type}`);
        return null;
      }

      const { part, task } = idParts;
      const targetId = task ? `card__part${part}-task${task}` : `card__part${part}-task-list-title`;

      return targetId;
    },

    parsePostId(type, id) {
      let part, task;
      let titleMatch, taskMatch;

      switch (type) {
        case 'title':
          titleMatch = id.match(/part-?(\d+)/);
          if (!titleMatch) return null;
          part = Number(titleMatch[1]);
          task = null;
          break;
        case 'task':
          taskMatch = id.match(/part-?(\d+)-task-?(\d+)?/);
          if (!taskMatch) return null;
          [part, task] = taskMatch.slice(1).map(Number);
          break;
        default:
          return null;
      }

      return { part, task };
    },

    scrollToElement(elemId, selectedId) {
      if (!elemId) {
        console.warn('No element ID provided for scrolling');
        return;
      }

      // Log available elements for debugging
      console.log(
        'Available elements with "card__" prefix:',
        Array.from(document.querySelectorAll('[id^="card__"]')).map((el) => el.id)
      );

      const element = document.getElementById(elemId);

      // Setting a proper scroll offset for header/navigation
      const SCROLL_OFFSET = 100;

      console.log(`Scrolling to element: ${elemId}`);

      // Get more accurate position calculation
      const rect = element.getBoundingClientRect();
      const offsetTop = window.pageYOffset + rect.top - SCROLL_OFFSET;

      // Use scrollIntoView for more consistent behavior across browsers
      try {
        // Set selectedId first to update UI immediately
        this.selectingOverride = selectedId;

        // Wait a moment to ensure DOM updates
        setTimeout(() => {
          // First try scrollIntoView with smooth behavior
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

          // Also apply window.scrollTo for more precise positioning with offset
          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth'
          });

          // Increase timeout to ensure scrolling completes
          setTimeout(() => {
            this.isScrolling = false;
          }, SCROLL_TIMEOUT);
        }, 50);
      } catch (error) {
        console.error('Error during scroll:', error);
        // Fallback to basic scrolling
        window.scrollTo(0, offsetTop);
        this.isScrolling = false;
      }
    },

    // Selection and display helpers
    getPostsByPart(part) {
      return this.posts.filter((post) => post.part === part);
    },

    partTitle(part) {
      return `Part ${this.partTitleIndexMap[part]} : ${PARTS[part] || part}`;
    },

    isSelected(id) {
      return this.selectingOverride ? this.selectingOverride === id : this.selectedId === id;
    },

    isPartSelected(partMeta) {
      const partId = `part-${this.partTitleIndexMap[partMeta]}`;
      const taskId = `${partId}-task-1`;

      if (partMeta === 'WORK_EXAMPLE') {
        return this.isSelected(partId);
      }

      if (partMeta === 'OVERVIEW') {
        return this.isSelected(partId) || this.isSelected(null) || this.isSelected(taskId);
      }

      return this.isSelected(taskId) || this.isSelected(partId);
    },

    // IntersectionObserver methods
    registerIntersectionObserver() {
      this.$nextTick(() => {
        const thresholdSets = Array.from({ length: 101 }, (_, i) => i / 100);

        const options = {
          root: null,
          rootMargin: '-200px 0px 0px 0px',
          threshold: thresholdSets
        };

        const callback = this.handleIntersection;
        this.observerOptions = { callback, options };
      });
    },

    handleIntersection(entries) {
      const entry = entries[0];

      if (!entry) return;

      if (entry.isIntersecting) {
        if (!this.entryList.some((item) => item.target === entry.target)) {
          this.entryList.push(entry);
        }
      } else if (!entry.isIntersecting || entry.intersectionRatio <= 0.2) {
        this.entryList = this.entryList.filter((item) => item.target !== entry.target);
      }
    },

    observePostInView() {
      this.$nextTick(() => {
        const elements = document.querySelectorAll('.observable');

        elements.forEach((el) => {
          const observer = new IntersectionObserver(
            this.observerOptions.callback,
            this.observerOptions.options
          );
          observer.observe(el);
          this.observers.push(observer);
        });
      });
    },

    disconnectObservers() {
      this.observers.forEach((observer) => observer.disconnect());
    },

    // Entry processing methods
    getSortedEntries() {
      return [...this.entryList].sort(this.compareEntries).concat(this.getDefaultEntries());
    },

    getDefaultEntries() {
      return [
        { target: { id: 'post-evaluation-assessment-submit' } },
        { target: { id: 'confirmation-evaluation-assessment' } }
      ];
    },

    compareEntries(a, b) {
      // Handle special entries
      if (this.isSpecialEntry(a.target.id)) return 1;
      if (this.isSpecialEntry(b.target.id)) return -1;

      const [partA, taskA] = this.parseCardId(a.target.id);
      const [partB, taskB] = this.parseCardId(b.target.id);

      // Handle null cases
      if (partA === null && partB !== null) return -1;
      if (partA !== null && partB === null) return 1;

      // Compare by part first, then by task
      if (partA !== partB) return partA - partB;
      return (taskA || 0) - (taskB || 0);
    },

    isSpecialEntry(id) {
      return (
        id === 'confirmation-evaluation-assessment' || id === 'post-evaluation-assessment-submit'
      );
    },

    parseCardId(id) {
      const match = id.match(/card__part?(\d+)-task-?(\d+)?/);
      if (match) {
        return match.slice(1).map(Number);
      }
      return [null, null];
    },

    parseEntryIdToNavId(entryId) {
      if (!entryId) return null;

      const idMatch = entryId.match(/card__part?(\d+)-task-?(\d+)?/);
      if (!idMatch) return entryId;

      const [part, task] = idMatch.slice(1).map(Number);
      return task ? `part-${part}-task-${task}` : `part-${part}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.assessment-navigator {
  width: 340px;
  background-color: #ffffff;
  border: 1px stroke gray;
  border-radius: 4px;
  font-weight: 500;
  color: #3c44b1;
}

.assessment-navigator ul {
  list-style-type: none;
  padding: 0;
  margin: 0;

  .part__work-example {
    margin-left: 16px;
  }
}

.assessment-navigator a {
  text-decoration: none;
  color: #333;
}

.assessment-navigator a:hover {
  text-decoration: underline;
}

.selectable-item {
  cursor: pointer;
  padding: 12px 16px;
  border-radius: 5px;
  max-width: 95%;
}

.part__work-example .selectable-item {
  padding-left: 0;
}

.selected {
  background: linear-gradient(91.61deg, #667ce8 0%, #744ba3 100%);
  color: white;
}

.task-list-item {
  list-style-type: disc;
  margin-left: 30px;
}

@media screen and (min-width: 992px) and (max-width: 1199px) {
  .assessment-navigator {
    max-width: 280px;
  }
}
</style>
